import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useIonAlert } from '@ionic/react';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../caremap/CareMapState';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { useMemberAccessRevocation } from '../share-caremap/sharing-and-access/useMemberAccessRevocation';

const REVOKE_ACCESS_INPUT_VALUE = 'revoke-access';

export const useDeleteHouseholdMember = ({ memberId, onDelete }: { memberId: string, onDelete?: () => void }) => {
    const tracking = useInstance(MixpanelService);

    const [presentAlert] = useIonAlert();

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const { findAccessLink, revokeAccess } = useMemberAccessRevocation();

    // ionic role is not strongly typed
    const destructiveRole = 'destructive';

    return () => {
        const member = careMapStateInstance.maybeGetHouseholdMember(memberId);
        const accessLink = member ? findAccessLink({
            memberId,
            email: careMapStateInstance.getContactMethodOrDefault(member, 'email').value,
            linkedUserId: member.linkedUserId
        }) : undefined;

        presentAlert({
            header: `Delete Inner Circle Member?`,
            message: accessLink
                ? `Inner Circle Member will be deleted from care map and cannot be recovered. They also have access to this care map, which stays unless you remove it below.`
                : `Inner Circle Member will be deleted from care map and cannot be recovered.`,
            cssClass: 'drawer-alert',
            // Only pass `inputs` when there's a link to revoke: Ionic's alert assumes inputs is an
            // array, so an explicit `inputs: undefined` crashes its render (overlay never shows).
            ...(accessLink ? {
                inputs: [{
                    type: 'checkbox',
                    label: 'Also remove their access to this care map',
                    value: REVOKE_ACCESS_INPUT_VALUE,
                    checked: false
                }]
            } : {}),
            onDidDismiss: (e) => {
                if (e.detail.role === destructiveRole && onDelete) {
                    onDelete();
                }
            },
            buttons: [{
                text: 'Delete',
                role: destructiveRole,
                handler: (data) => {
                    const alsoRemoveAccess = Boolean(accessLink) && Array.isArray(data) && data.includes(REVOKE_ACCESS_INPUT_VALUE);
                    if (alsoRemoveAccess && accessLink) {
                        revokeAccess(accessLink);
                    }
                    careMapStateInstance.removeHouseholdMember(memberId);
                    tracking.track('Household Member Removed', () => ({ access_also_removed: alsoRemoveAccess }));
                }
            }, {
                text: 'Cancel',
                role: 'cancel'
            }]
        });

    };
};
